import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Truck, AlertCircle } from "lucide-react";
import { useCalculateShipping, useCreateShipment, useIntegrationStatus } from "@/hooks/useMelhorEnvio";
import { useMandaBemStatus, useMandaBemCalculateShipping, useMandaBemCreateShipment } from "@/hooks/useMandaBem";
import { useSuperFreteStatus, useSuperFreteCalculateShipping, useSuperFreteCreateShipment } from "@/hooks/useSuperFrete";
import { useCorreiosStatus, useCorreiosCalculateShipping, useCorreiosCreateShipment } from "@/hooks/useCorreios";
import { useShippingCustomization } from "@/hooks/useShippingCustomization";
import { toast } from "@/hooks/use-toast";
import { Sale } from "@/hooks/useSales";
import { Product } from "@/hooks/useProducts";

type ShippingProvider = "melhor_envio" | "manda_bem" | "super_frete" | "correios";

interface ShippingAddress {
  cep: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
}

interface ShippingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sale: Sale | null;
  storeAddress: {
    name: string;
    phone: string;
    email: string;
    document: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    cep: string;
  } | null;
  fallbackAddress?: ShippingAddress | null;
  products?: Product[];
}

interface ShippingService {
  id: number | string;
  name: string;
  price: string;
  custom_price?: string;
  discount?: string;
  currency: string;
  delivery_time: number;
  delivery_range: {
    min: number;
    max: number;
  };
  company: {
    id: number;
    name: string;
    picture: string;
  };
  provider?: string;
}

export function ShippingModal({ open, onOpenChange, sale, storeAddress, fallbackAddress, products }: ShippingModalProps) {
  const [services, setServices] = useState<ShippingService[]>([]);
  const [selectedService, setSelectedService] = useState<ShippingService | null>(null);
  const [activeProvider, setActiveProvider] = useState<ShippingProvider | null>(null);

  const { data: melhorEnvioStatus } = useIntegrationStatus();
  const { data: mandaBemStatus } = useMandaBemStatus();
  const { data: superFreteStatus } = useSuperFreteStatus();
  const { data: correiosStatus } = useCorreiosStatus();

  const meCalculateMutation = useCalculateShipping();
  const meCreateShipmentMutation = useCreateShipment();
  const mbCalculateMutation = useMandaBemCalculateShipping();
  const mbCreateShipmentMutation = useMandaBemCreateShipment();
  const sfCalculateMutation = useSuperFreteCalculateShipping();
  const sfCreateShipmentMutation = useSuperFreteCreateShipment();
  const correiosCalculateMutation = useCorreiosCalculateShipping();
  const correiosCreateShipmentMutation = useCorreiosCreateShipment();

  const melhorEnvioConnected = melhorEnvioStatus?.connected ?? false;
  const mandaBemConnected = mandaBemStatus?.connected ?? false;
  const superFreteConnected = superFreteStatus?.connected ?? false;
  const correiosConnected = correiosStatus?.connected ?? false;
  const anyConnected = melhorEnvioConnected || mandaBemConnected || superFreteConnected || correiosConnected;
  const connectedProviders = [
    melhorEnvioConnected && "melhor_envio" as ShippingProvider,
    mandaBemConnected && "manda_bem" as ShippingProvider,
    superFreteConnected && "super_frete" as ShippingProvider,
    correiosConnected && "correios" as ShippingProvider,
  ].filter(Boolean) as ShippingProvider[];

  const customerAddress = sale?.customer?.cep ? {
    cep: sale.customer.cep,
    street: sale.customer.street || '',
    number: sale.customer.number || '',
    complement: sale.customer.complement || '',
    neighborhood: sale.customer.neighborhood || '',
    city: sale.customer.city || '',
    state: sale.customer.state || '',
  } : fallbackAddress;

  useEffect(() => {
    if (!open) return;
    if (connectedProviders.length === 1) {
      setActiveProvider(connectedProviders[0]);
    } else if (connectedProviders.length > 1 && !activeProvider) {
      setActiveProvider(connectedProviders[0]);
    }
  }, [open, connectedProviders.length, activeProvider]);

  const { settings } = useShippingCustomization();
  const emb = settings.embalagem_padrao;

  const meCalculateRef = useRef(meCalculateMutation);
  meCalculateRef.current = meCalculateMutation;
  const mbCalculateRef = useRef(mbCalculateMutation);
  mbCalculateRef.current = mbCalculateMutation;

  const getProductDims = (productId: string | null) => {
    const p = products?.find(pr => pr.id === productId);
    return {
      width: p?.width || 0,
      height: p?.height || 0,
      depth: p?.depth || 0,
      weight: p?.weight ? p.weight / 1000 : 0,
    };
  };

  const hasRealDims = sale?.sale_items?.some(i => {
    const d = getProductDims(i.product_id);
    return d.width > 0 && d.height > 0 && d.depth > 0;
  }) || false;

  const getDims = (productId: string | null) => {
    const d = getProductDims(productId);
    return {
      width: d.width || emb.largura,
      height: d.height || emb.altura,
      depth: d.depth || emb.comprimento,
      weight: d.weight,
    };
  };

  const productsWeight = sale?.sale_items?.reduce(
    (sum, item) => sum + getDims(item.product_id).weight * item.quantity, 0
  ) || 0;
  const totalWeight = productsWeight > 0 ? productsWeight : emb.peso;
  const maxWidth = Math.max(
    ...(sale?.sale_items?.map(i => getDims(i.product_id).width) || [emb.largura])
  );
  const maxDepth = Math.max(
    ...(sale?.sale_items?.map(i => getDims(i.product_id).depth) || [emb.comprimento])
  );
  const totalHeight = sale?.sale_items?.reduce(
    (sum, item) => sum + getDims(item.product_id).height * item.quantity, 0
  ) || emb.altura;
  const weightInGrams = Math.round(Math.max(totalWeight, 0.3) * 1000);

  const calculateShipping = useCallback(async () => {
    if (!sale || !storeAddress || !customerAddress?.cep || !activeProvider) return;

    try {
      if (activeProvider === "melhor_envio") {
        const result = await meCalculateRef.current.mutateAsync({
          from: {
            postal_code: storeAddress.cep.replace(/\D/g, ""),
          },
          to: {
            postal_code: customerAddress.cep.replace(/\D/g, ""),
          },
          products: [{
            width: maxWidth,
            height: Math.max(totalHeight, emb.altura),
            length: maxDepth,
            weight: Math.max(totalWeight, 0.3),
            insurance_value: Number(sale.total) || 0,
            quantity: 1,
          }],
        });
        setServices(result);
      } else if (activeProvider === "manda_bem") {
        const result = await mbCalculateRef.current.mutateAsync({
          cepOrigem: storeAddress.cep.replace(/\D/g, ""),
          cepDestino: customerAddress.cep.replace(/\D/g, ""),
          peso: weightInGrams,
          altura: hasRealDims ? Math.max(totalHeight, emb.altura) : 0,
          largura: hasRealDims ? maxWidth : 0,
          comprimento: hasRealDims ? maxDepth : 0,
        });
        setServices(result);
      } else if (activeProvider === "super_frete") {
        const result = await sfCalculateMutation.mutateAsync({
          cepOrigem: storeAddress.cep.replace(/\D/g, ""),
          cepDestino: customerAddress.cep.replace(/\D/g, ""),
          peso: weightInGrams,
          altura: hasRealDims ? Math.max(totalHeight, emb.altura) : 0,
          largura: hasRealDims ? maxWidth : 0,
          comprimento: hasRealDims ? maxDepth : 0,
        });
        setServices(result);
      } else if (activeProvider === "correios") {
        const result = await correiosCalculateMutation.mutateAsync({
          cepOrigem: storeAddress.cep.replace(/\D/g, ""),
          cepDestino: customerAddress.cep.replace(/\D/g, ""),
          peso: weightInGrams,
          altura: hasRealDims ? Math.max(totalHeight, emb.altura) : 0,
          largura: hasRealDims ? maxWidth : 0,
          comprimento: hasRealDims ? maxDepth : 0,
        });
        setServices(result);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Não foi possível calcular as opções de envio.";
      toast({
        title: "Erro ao calcular frete",
        description: message,
        variant: "destructive",
      });
    }
  }, [customerAddress?.cep, sale, storeAddress, activeProvider, maxWidth, maxDepth, totalHeight, totalWeight, weightInGrams, emb]);

  useEffect(() => {
    if (open && sale && storeAddress && activeProvider && anyConnected) {
      setServices([]);
      setSelectedService(null);
      calculateShipping();
    }
  }, [open, sale, storeAddress, activeProvider, anyConnected, calculateShipping]);

  const handleProviderChange = (provider: ShippingProvider) => {
    setActiveProvider(provider);
    setServices([]);
    setSelectedService(null);
  };

  const handleCreateShipment = async () => {
    if (!selectedService || !sale || !storeAddress || !customerAddress?.cep || !activeProvider) return;

    try {
      if (activeProvider === "melhor_envio") {
        await meCreateShipmentMutation.mutateAsync({
          sale_id: sale.id,
          service: {
            id: selectedService.id,
            name: `${selectedService.company.name} - ${selectedService.name}`,
            price: parseFloat(selectedService.price),
          },
          from: {
            name: storeAddress.name,
            phone: storeAddress.phone.replace(/\D/g, ""),
            email: storeAddress.email,
            document: storeAddress.document.replace(/\D/g, ""),
            address: storeAddress.street,
            number: storeAddress.number,
            complement: storeAddress.complement || "",
            district: storeAddress.neighborhood,
            city: storeAddress.city,
            country_id: "BR",
            postal_code: storeAddress.cep.replace(/\D/g, ""),
          },
          to: {
            name: sale.customer?.name || "",
            phone: sale.customer?.whatsapp?.replace(/\D/g, "") || "",
            email: sale.customer?.email || "",
            document: sale.customer?.document?.replace(/\D/g, "") || "",
            address: customerAddress.street || "",
            number: customerAddress.number || "",
            complement: customerAddress.complement || "",
            district: customerAddress.neighborhood || "",
            city: customerAddress.city || "",
            state_abbr: customerAddress.state || "",
            country_id: "BR",
            postal_code: customerAddress.cep.replace(/\D/g, ""),
          },
          products: sale.sale_items?.map(item => ({
            name: item.product_name,
            quantity: item.quantity,
            unitary_value: Number(item.unit_price),
          })) || [],
          volumes: [{
            height: Math.max(totalHeight, emb.altura),
            width: maxWidth,
            length: maxDepth,
            weight: Math.max(totalWeight, 0.3),
          }],
        });
      } else if (activeProvider === "manda_bem") {
        await mbCreateShipmentMutation.mutateAsync({
          sale_id: sale.id,
          service: {
            id: selectedService.id,
            name: `${selectedService.company.name} - ${selectedService.name}`,
            price: parseFloat(selectedService.price),
          },
          from: {
            cep: storeAddress.cep.replace(/\D/g, ""),
            name: storeAddress.name,
          },
          to: {
            name: sale.customer?.name || "",
            cep: customerAddress.cep.replace(/\D/g, ""),
            document: sale.customer?.document?.replace(/\D/g, "") || "",
            email: sale.customer?.email || "",
            street: customerAddress.street || "",
            number: customerAddress.number || "",
            complement: customerAddress.complement || "",
            neighborhood: customerAddress.neighborhood || "",
            city: customerAddress.city || "",
            state: customerAddress.state || "",
          },
          products: sale.sale_items?.map(item => ({
            name: item.product_name,
            quantity: item.quantity,
            unitary_value: Number(item.unit_price),
          })) || [],
          peso: weightInGrams,
          altura: hasRealDims ? Math.max(totalHeight, emb.altura) : 0,
          largura: hasRealDims ? maxWidth : 0,
          comprimento: hasRealDims ? maxDepth : 0,
        });
      } else if (activeProvider === "super_frete") {
        const sfResult = (await sfCreateShipmentMutation.mutateAsync({
          sale_id: sale.id,
          service: {
            id: selectedService.id,
            name: `SuperFrete - ${selectedService.name}`,
            price: parseFloat(String(selectedService.price)),
          },
          from: {
            cep: storeAddress.cep.replace(/\D/g, ""),
            name: storeAddress.name,
            phone: storeAddress.phone.replace(/\D/g, ""),
            email: storeAddress.email,
            document: storeAddress.document.replace(/\D/g, ""),
            street: storeAddress.street,
            number: storeAddress.number,
            complement: storeAddress.complement || "",
            neighborhood: storeAddress.neighborhood,
            city: storeAddress.city,
            state: storeAddress.state,
          },
          to: {
            name: sale.customer?.name || "",
            cep: customerAddress.cep.replace(/\D/g, ""),
            document: sale.customer?.document?.replace(/\D/g, "") || "",
            email: sale.customer?.email || "",
            street: customerAddress.street || "",
            number: customerAddress.number || "",
            complement: customerAddress.complement || "",
            neighborhood: customerAddress.neighborhood || "",
            city: customerAddress.city || "",
            state: customerAddress.state || "",
          },
          products: sale.sale_items?.map(item => ({
            name: item.product_name,
            quantity: item.quantity,
            unitary_value: Number(item.unit_price),
          })) || [],
          peso: weightInGrams,
          altura: hasRealDims ? Math.max(totalHeight, emb.altura) : 0,
          largura: hasRealDims ? maxWidth : 0,
          comprimento: hasRealDims ? maxDepth : 0,
        })) as {
          label_pending?: boolean;
          pending_reason?: string | null;
          print_error?: string | null;
          order_status?: string | null;
        } | undefined;
        if (sfResult?.label_pending) {
          const reason = sfResult.pending_reason
            || (sfResult.print_error ? `Falha no SuperFrete: ${sfResult.print_error}` : null)
            || 'O envio foi criado no SuperFrete, mas a etiqueta ainda não foi liberada. Use o botão "Gerar etiqueta" na venda para tentar novamente.';
          toast({
            title: "Envio criado, etiqueta pendente",
            description: reason,
            variant: "destructive",
          });
          onOpenChange(false);
          return;
        }
      } else if (activeProvider === "correios") {
        await correiosCreateShipmentMutation.mutateAsync({
          sale_id: sale.id,
          service: {
            id: selectedService.id,
            name: `Correios - ${selectedService.name}`,
            price: parseFloat(String(selectedService.price)),
          },
          from: {
            name: storeAddress.name,
            document: storeAddress.document.replace(/\D/g, ""),
            cep: storeAddress.cep.replace(/\D/g, ""),
            street: storeAddress.street,
            number: storeAddress.number,
            complement: storeAddress.complement || "",
            neighborhood: storeAddress.neighborhood,
            city: storeAddress.city,
            state: storeAddress.state,
          },
          to: {
            name: sale.customer?.name || "",
            document: sale.customer?.document?.replace(/\D/g, "") || "",
            email: sale.customer?.email || "",
            cep: customerAddress.cep.replace(/\D/g, ""),
            street: customerAddress.street || "",
            number: customerAddress.number || "",
            complement: customerAddress.complement || "",
            neighborhood: customerAddress.neighborhood || "",
            city: customerAddress.city || "",
            state: customerAddress.state || "",
          },
          products: sale.sale_items?.map(item => ({
            name: item.product_name,
            quantity: item.quantity,
            unitary_value: Number(item.unit_price),
          })) || [],
          peso: weightInGrams,
          altura: hasRealDims ? Math.max(totalHeight, emb.altura) : 0,
          largura: hasRealDims ? maxWidth : 0,
          comprimento: hasRealDims ? maxDepth : 0,
        });
      }

      toast({
        title: "Etiqueta gerada!",
        description: "A etiqueta de envio foi gerada com sucesso.",
      });

      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Não foi possível gerar a etiqueta de envio.";
      toast({
        title: "Erro ao gerar etiqueta",
        description: message,
        variant: "destructive",
      });
    }
  };

  const formatCurrency = (value: string) => {
    return parseFloat(value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const isCalculating = meCalculateMutation.isPending || mbCalculateMutation.isPending || sfCalculateMutation.isPending || correiosCalculateMutation.isPending;
  const isCreating = meCreateShipmentMutation.isPending || mbCreateShipmentMutation.isPending || sfCreateShipmentMutation.isPending || correiosCreateShipmentMutation.isPending;

  if (!anyConnected) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Gerar Etiqueta de Envio</DialogTitle>
            <DialogDescription className="sr-only">Nenhum provedor de envio conectado</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground" />
            <p className="text-center text-muted-foreground">
              Conecte o Melhor Envio, Manda Bem, SuperFrete ou Correios para gerar etiquetas.
              <br />
              Acesse a página de Integrações para conectar.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!customerAddress?.cep) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Gerar Etiqueta de Envio</DialogTitle>
            <DialogDescription className="sr-only">CEP do cliente não cadastrado</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground" />
            <p className="text-center text-muted-foreground">
              O cliente desta venda não possui CEP cadastrado.
              <br />
              Atualize os dados do cliente para gerar a etiqueta.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!storeAddress) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Gerar Etiqueta de Envio</DialogTitle>
            <DialogDescription className="sr-only">Endereço da loja não configurado</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground" />
            <p className="text-center text-muted-foreground">
              Você precisa configurar o endereço da loja nas Configurações
              <br />
              antes de gerar etiquetas de envio.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gerar Etiqueta de Envio</DialogTitle>
          <DialogDescription>
            Selecione o serviço de envio desejado
          </DialogDescription>
        </DialogHeader>

        {connectedProviders.length > 1 && (
          <div className="flex gap-2">
            {melhorEnvioConnected && (
              <Button variant={activeProvider === "melhor_envio" ? "default" : "outline"} size="sm" className="h-8 text-xs" onClick={() => handleProviderChange("melhor_envio")}>Melhor Envio</Button>
            )}
            {mandaBemConnected && (
              <Button variant={activeProvider === "manda_bem" ? "default" : "outline"} size="sm" className="h-8 text-xs" onClick={() => handleProviderChange("manda_bem")}>Manda Bem</Button>
            )}
            {superFreteConnected && (
              <Button variant={activeProvider === "super_frete" ? "default" : "outline"} size="sm" className="h-8 text-xs" onClick={() => handleProviderChange("super_frete")}>SuperFrete</Button>
            )}
            {correiosConnected && (
              <Button variant={activeProvider === "correios" ? "default" : "outline"} size="sm" className="h-8 text-xs" onClick={() => handleProviderChange("correios")}>Correios</Button>
            )}
          </div>
        )}

        {isCalculating ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Calculando opções de frete...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground" />
            <p className="text-center text-muted-foreground">
              Nenhum serviço de envio disponível para este destino.
            </p>
            <Button variant="outline" onClick={calculateShipping}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {services.map((service) => (
                <div
                  key={`${service.provider || activeProvider}-${service.id}`}
                  className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedService?.id === service.id
                      ? "border-primary bg-primary/5"
                      : "hover:border-muted-foreground/50"
                  }`}
                  onClick={() => setSelectedService(service)}
                >
                  <div className="flex-shrink-0">
                    {service.company.picture ? (
                      <img
                        src={service.company.picture}
                        alt={service.company.name}
                        className="h-10 w-10 object-contain"
                      />
                    ) : (
                      <Truck className="h-10 w-10 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {service.company.name} - {service.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {service.delivery_range.min === service.delivery_range.max
                        ? `${service.delivery_range.min} dias úteis`
                        : `${service.delivery_range.min}-${service.delivery_range.max} dias úteis`
                      }
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">
                      {formatCurrency(service.price)}
                    </p>
                    {service.discount && parseFloat(service.discount) > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        -{formatCurrency(service.discount)}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleCreateShipment}
                disabled={!selectedService || isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  "Gerar Etiqueta"
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
